import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, Mail, MessageCircle, CheckCircle, XCircle, FileText, Clock, User, Calendar, Package, Phone, Building, IndianRupee, Printer, Truck, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { purchaseOrdersAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const PurchaseOrderDetailPage = () => {
  const { businessId, orderId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && orderId) {
      fetchOrder();
    }
  }, [bid, orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await purchaseOrdersAPI.get(orderId);
      setOrder(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load order'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setConfirmModal({
      title: 'Confirm Purchase Order',
      description: `Confirm ${order?.order_number}? This will lock the order for editing.`,
      confirmLabel: 'Confirm Order',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CheckCircle,
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseOrdersAPI.confirm(orderId);
          toast.success('Purchase order confirmed');
          fetchOrder();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm'));
        }
      },
    });
  };

  const handleCancel = () => {
    setCancelReason('');
    setConfirmModal({
      title: 'Cancel Purchase Order',
      description: `Cancel ${order?.order_number}? This cannot be undone.`,
      confirmLabel: 'Cancel Order',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      isCancelFlow: true,
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await purchaseOrdersAPI.cancel(orderId, reason);
          toast.success('Purchase order cancelled');
          setCancelReason('');
          fetchOrder();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await purchaseOrdersAPI.pdf(orderId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${order?.order_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await purchaseOrdersAPI.sendEmail(orderId);
      toast.success(response.data.message || 'Email sent');
      if (response.data.note) toast(response.data.note, { icon: 'info' });
      fetchOrder();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateReceive = () => {
    navigate(`/business/${bid}/purchase-receives/new?po=${orderId}`);
  };

  const handleBack = () => {
    navigate(`/business/${bid}/purchase-orders`);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Purchase Order not found</p>
          <button onClick={handleBack} className="mt-4 text-teal-600 hover:underline text-[14px]">
            ← Back to Purchase Orders
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Draft' },
    CONFIRMED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Confirmed' },
    PARTIALLY_RECEIVED: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Package, label: 'Partial' },
    RECEIVED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Received' },
    CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Cancelled' },
  };

  const statusBadge = statusConfig[order.status] || statusConfig.DRAFT;
  const StatusIcon = statusBadge.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const items = order.items || [];

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Order Info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{order.order_number}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusBadge.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Created on {formatDate(order.created_at)} • {items.length} item{items.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {order.status === 'DRAFT' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/purchase-orders/${orderId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleConfirm}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirm Order
                  </button>
                </>
              )}
              {(order.status === 'CONFIRMED' || order.status === 'PARTIALLY_RECEIVED') && (
                <>
                  <button
                    onClick={handleCreateReceive}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
                  >
                    <Package className="w-4 h-4" />
                    Convert → Purchase Receive
                  </button>
                  <button
                    onClick={() => navigate(`/business/${bid}/purchase-invoices/new?source_type=PO&source_id=${orderId}&supplier_id=${order.supplier_id || order.party_id}`)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-blue-600/20 active:scale-[0.98]"
                  >
                    <FileText className="w-4 h-4" />
                    Convert → Purchase Invoice
                  </button>
                </>
              )}
              {order.status === 'CONFIRMED' && (
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel
                </button>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button onClick={handleDownloadPdf} className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]" title="Download PDF">
                <Download className="w-4 h-4" />
              </button>
              <button onClick={handleSendEmail} disabled={sendingEmail} className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed" title="Send Email">
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - Premium 3-Column Layout */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Supplier & Delivery (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Supplier Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Supplier</h2>
                </div>
                
                {(order.supplier_name || order.party_name) ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">
                          {(order.supplier_name || order.party_name)?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{order.supplier_name || order.party_name}</p>
                        <p className="text-[12px] text-slate-500">Supplier</p>
                      </div>
                    </div>

                    {(order.supplier_phone || order.party_phone) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {order.supplier_phone || order.party_phone}
                      </div>
                    )}

                    {(order.supplier_email || order.party_email) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {order.supplier_email || order.party_email}
                      </div>
                    )}

                    {(order.supplier_gstin || order.party_gstin) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building className="w-4 h-4 text-slate-400" />
                        GSTIN: {order.supplier_gstin || order.party_gstin}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[13px] text-slate-500">No supplier selected</p>
                  </div>
                )}
              </div>

              {/* Order Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Order Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Order Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(order.order_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Expected Delivery</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(order.expected_delivery_date || order.expected_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Payment Terms</span>
                    <span className="text-[13px] font-medium text-slate-700">{order.payment_terms || 'Net 30'}</span>
                  </div>
                  {order.reference_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Reference</span>
                      <span className="text-[13px] font-medium text-slate-700">{order.reference_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Receive Status Card */}
              {(order.status === 'PARTIALLY_RECEIVED' || order.status === 'RECEIVED') && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Truck className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Receive Status</h2>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Received Value</span>
                      <span className="text-[13px] font-medium text-emerald-600">₹{(order.received_value || order.received_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Pending Value</span>
                      <span className="text-[13px] font-medium text-amber-600">₹{((order.grand_total || 0) - (order.received_value || order.received_amount || 0)).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Order Items</h2>
                      <p className="text-[12px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
                
                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Details</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tax</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-5 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-5 py-4">
                              <p className="text-[14px] font-medium text-slate-800">{item.item_name}</p>
                              {item.hsn_code && (
                                <p className="text-[11px] text-slate-400 mt-0.5">HSN: {item.hsn_code}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-700">
                                {item.quantity} {item.unit || 'Nos'}
                              </span>
                              {item.received_quantity > 0 && (
                                <p className="text-[11px] text-emerald-600 mt-1">Received: {item.received_quantity}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              ₹{(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              {item.tax_rate || 0}%
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this order</p>
                  </div>
                )}

                {order.notes && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Notes</p>
                    <p className="text-[13px] text-amber-800">{order.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card - Premium Purple */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 shadow-sm border border-purple-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Order Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{(order.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {order.discount_total > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Discount</span>
                      <span className="text-red-600">- ₹{order.discount_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-purple-600 font-medium">+ ₹{(order.tax_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-purple-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[22px] font-bold text-purple-600">
                        ₹{(order.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Timeline Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Status</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${order.status === 'DRAFT' ? 'bg-amber-500' : 'bg-emerald-500'}`}></div>
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">Created</p>
                      <p className="text-[11px] text-slate-500">{formatDate(order.created_at)}</p>
                    </div>
                  </div>
                  
                  {order.status !== 'DRAFT' && order.status !== 'CANCELLED' && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Confirmed</p>
                        <p className="text-[11px] text-slate-500">Order sent to supplier</p>
                      </div>
                    </div>
                  )}
                  
                  {order.status === 'RECEIVED' && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Received</p>
                        <p className="text-[11px] text-slate-500">All items received</p>
                      </div>
                    </div>
                  )}
                  
                  {order.status === 'CANCELLED' && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Cancelled</p>
                        <p className="text-[11px] text-slate-500">{order.cancellation_reason || 'No reason provided'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              {order.status === 'DRAFT' && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                  <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate(`/business/${bid}/purchase-orders/${orderId}/edit`)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                    >
                      <Edit className="w-4 h-4 text-slate-600" />
                      <span className="text-[13px] font-medium text-slate-700">Edit Order</span>
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors duration-200"
                    >
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-[13px] font-medium text-emerald-700">Confirm Order</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        confirmDisabled={confirmModal?.isCancelFlow ? !cancelReason.trim() : false}
        onConfirm={() => confirmModal?.onConfirm?.(cancelReason)}
        onClose={() => { setConfirmModal(null); setCancelReason(''); }}
      >
        {confirmModal?.isCancelFlow && (
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Supplier unable to deliver, found better pricing, order no longer needed..."
            rows={3}
            className="w-full mt-3 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none"
          />
        )}
      </ConfirmModal>
    </div>
  );
};

export default PurchaseOrderDetailPage;
